import { useState, useEffect, useCallback } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import {
  AlertCircle, CheckCircle2, Eye, Search, Clock, ArrowLeft,
  Monitor, Globe, X, ChevronDown, ChevronRight, MessageSquare,
  Building2, GitBranch, User,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  new: 'bg-red-100 text-red-700 border-red-200',
  reviewed: 'bg-amber-100 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const STATUS_ICONS = {
  new: AlertCircle,
  reviewed: Eye,
  resolved: CheckCircle2,
};

export default function ErrorLogPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ new: 0, reviewed: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [noteInput, setNoteInput] = useState('');

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter !== 'all') params.status = filter;
      const res = await api.get('/error-reports', { params });
      setReports(res.data.reports || []);
      setTotal(res.data.total || 0);
      if (res.data.counts) setCounts(res.data.counts);
    } catch {
      toast.error('Failed to load error reports');
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const updateStatus = async (reportId, status) => {
    try {
      await api.patch(`/error-reports/${reportId}/status`, {
        status,
        admin_note: noteInput || null,
      });
      toast.success(`Marked as ${status}`);
      setNoteInput('');
      loadReports();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const filtered = search
    ? reports.filter(r =>
        r.error_code?.toLowerCase().includes(search.toLowerCase()) ||
        r.error_message?.toLowerCase().includes(search.toLowerCase()) ||
        r.page?.toLowerCase().includes(search.toLowerCase()) ||
        r.reported_by_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.organization_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.branch_name?.toLowerCase().includes(search.toLowerCase())
      )
    : reports;

  if (!user?.is_super_admin) {
    return (
      <div className="p-8 text-center text-slate-400">
        <AlertCircle size={36} className="mx-auto mb-3 text-slate-300" />
        <p className="font-medium">Super admin access required</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fadeIn" data-testid="error-log-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/superadmin')} className="h-8 px-2">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Platform Error Log
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            User-reported errors across all tenants
          </p>
        </div>
        {/* Status counters */}
        <div className="flex gap-2">
          <div className="text-center px-3 py-1 rounded-lg bg-red-50 border border-red-200">
            <p className="text-lg font-bold text-red-700">{counts.new}</p>
            <p className="text-[9px] text-red-500 uppercase">New</p>
          </div>
          <div className="text-center px-3 py-1 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-lg font-bold text-amber-700">{counts.reviewed}</p>
            <p className="text-[9px] text-amber-500 uppercase">Reviewed</p>
          </div>
          <div className="text-center px-3 py-1 rounded-lg bg-emerald-50 border border-emerald-200">
            <p className="text-lg font-bold text-emerald-700">{counts.resolved}</p>
            <p className="text-[9px] text-emerald-500 uppercase">Resolved</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by code, error, tenant, branch, user..."
            className="pl-8 h-9"
            data-testid="error-log-search"
          />
        </div>
        <div className="flex gap-1">
          {['all', 'new', 'reviewed', 'resolved'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                filter === s
                  ? 'border-[#1A4D2E] bg-[#1A4D2E]/5 text-[#1A4D2E]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {s === 'all' ? `All (${total})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${counts[s] || 0})`}
            </button>
          ))}
        </div>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle2 size={36} className="mx-auto text-emerald-200 mb-3" />
          <p className="text-slate-400 text-sm">
            {filter !== 'all' ? `No ${filter} error reports` : 'No error reports — all clear'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(report => {
            const isExpanded = expanded === report.id;
            const StatusIcon = STATUS_ICONS[report.status] || AlertCircle;
            const ts = report.created_at ? new Date(report.created_at) : null;

            return (
              <Card
                key={report.id}
                className={`border transition-all ${
                  report.status === 'new' ? 'border-red-200 bg-red-50/30' : 'border-slate-200'
                }`}
                data-testid={`error-report-${report.error_code}`}
              >
                {/* Collapsed row */}
                <button
                  className="w-full text-left px-4 py-3 flex items-center gap-3"
                  onClick={() => setExpanded(isExpanded ? null : report.id)}
                  data-testid={`expand-${report.error_code}`}
                >
                  {isExpanded
                    ? <ChevronDown size={14} className="text-slate-400 shrink-0" />
                    : <ChevronRight size={14} className="text-slate-400 shrink-0" />
                  }

                  <Badge className={`text-[10px]  shrink-0 ${STATUS_COLORS[report.status]}`}>
                    <StatusIcon size={10} className="mr-1" />
                    {report.error_code}
                  </Badge>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {report.error_message?.slice(0, 120)}
                      {report.error_message?.length > 120 ? '...' : ''}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400 flex-wrap">
                      {/* Tenant */}
                      <span className="flex items-center gap-1 text-indigo-600 font-medium">
                        <Building2 size={10} /> {report.organization_name || 'Unknown Org'}
                      </span>
                      {/* Branch */}
                      {report.branch_name && (
                        <span className="flex items-center gap-1">
                          <GitBranch size={10} /> {report.branch_name}
                        </span>
                      )}
                      {/* Page */}
                      <span className="flex items-center gap-1">
                        <Globe size={10} /> {report.page}
                      </span>
                      {/* User */}
                      <span className="flex items-center gap-1">
                        <User size={10} /> {report.reported_by_name}
                      </span>
                      {/* Time */}
                      {ts && (
                        <span className="flex items-center gap-1">
                          <Clock size={10} />
                          {ts.toLocaleDateString()} {ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>

                  <Badge className={`text-[9px] shrink-0 ${STATUS_COLORS[report.status]}`}>
                    {report.status}
                  </Badge>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <CardContent className="pt-0 pb-4 px-4">
                    <Separator className="mb-3" />

                    {/* Context grid */}
                    <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-xs mb-4">
                      <div>
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">Tenant</span>
                        <p className="font-medium text-indigo-700">{report.organization_name || '—'}</p>
                        <p className="text-[10px] text-slate-400">{report.organization_id || '—'}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">Branch</span>
                        <p className="font-medium text-slate-700">{report.branch_name || '—'}</p>
                        <p className="text-[10px] text-slate-400">{report.branch_id || '—'}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">Reporter</span>
                        <p className="font-medium text-slate-700">{report.reported_by_name}</p>
                        <p className="text-[10px] text-slate-400">{report.reported_by_email} ({report.reported_by_role})</p>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">Page</span>
                        <p className="text-slate-700">{report.page}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">Action</span>
                        <p className="text-slate-700">{report.action}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">Component</span>
                        <p className="text-slate-700">{report.component || '—'}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">HTTP Status</span>
                        <p className="text-slate-700">{report.http_status || '—'}</p>
                      </div>
                      {report.request_url && (
                        <div className="col-span-2">
                          <span className="text-slate-400 uppercase text-[10px] font-semibold">Request</span>
                          <p className="text-slate-700">{report.request_method} {report.request_url}</p>
                        </div>
                      )}
                    </div>

                    {/* Full error */}
                    <div className="mb-3">
                      <span className="text-slate-400 uppercase text-[10px] font-semibold">Full Error Message</span>
                      <pre className="mt-1 p-3 bg-slate-900 text-red-400 text-[11px] rounded-lg overflow-x-auto whitespace-pre-wrap max-h-48">
                        {report.error_message}
                      </pre>
                    </div>

                    {/* Stack trace */}
                    {report.error_stack && (
                      <div className="mb-3">
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">Stack Trace</span>
                        <pre className="mt-1 p-3 bg-slate-900 text-slate-400 text-[10px] rounded-lg overflow-x-auto whitespace-pre-wrap max-h-36">
                          {report.error_stack}
                        </pre>
                      </div>
                    )}

                    {/* Browser */}
                    {report.browser_info && (
                      <div className="mb-3">
                        <span className="text-slate-400 uppercase text-[10px] font-semibold">Browser / Device</span>
                        <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                          <Monitor size={11} /> {report.browser_info}
                        </p>
                      </div>
                    )}

                    {/* Admin note */}
                    {report.admin_note && (
                      <div className="mb-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                        <span className="text-blue-600 uppercase text-[10px] font-semibold flex items-center gap-1">
                          <MessageSquare size={10} /> Admin Note
                        </span>
                        <p className="text-xs text-blue-800 mt-1">{report.admin_note}</p>
                        {report.resolved_by && (
                          <p className="text-[10px] text-blue-400 mt-1">— {report.resolved_by}</p>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      <Input
                        value={noteInput}
                        onChange={e => setNoteInput(e.target.value)}
                        placeholder="Add note (root cause, fix applied, etc.)..."
                        className="h-8 text-xs flex-1"
                        data-testid={`admin-note-${report.error_code}`}
                      />
                      {report.status !== 'reviewed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => updateStatus(report.id, 'reviewed')}
                          data-testid={`mark-reviewed-${report.error_code}`}
                        >
                          <Eye size={12} className="mr-1" /> Reviewed
                        </Button>
                      )}
                      {report.status !== 'resolved' && (
                        <Button
                          size="sm"
                          className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => updateStatus(report.id, 'resolved')}
                          data-testid={`mark-resolved-${report.error_code}`}
                        >
                          <CheckCircle2 size={12} className="mr-1" /> Resolve
                        </Button>
                      )}
                      {report.status === 'resolved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs"
                          onClick={() => updateStatus(report.id, 'new')}
                          data-testid={`reopen-${report.error_code}`}
                        >
                          Re-open
                        </Button>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
